/**
 * ActionBar — left/top pane of the Matter AI Workspace.
 *
 * Renders the six Phase 1a actions as buttons grouped by family.
 * Selecting an action sets the active action on the parent shell;
 * actions are only triggered (i.e. a /run is fired) when the user
 * clicks the "Run" button on the FollowUpInput pane.  This is
 * deliberate — Spellbook-style UX, not chat-style auto-fire.
 */
import { Button } from "../../components/ui/button";

const FAMILY_LABELS = {
    review: "Review",
    draft: "Draft",
    compare: "Compare",
    ask: "Ask",
};

const FAMILY_ORDER = ["review", "draft", "compare", "ask"];

export default function ActionBar({ actions, activeActionKey, onSelectAction, disabled }) {
    if (!Array.isArray(actions) || actions.length === 0) {
        return (
            <div className="text-sm text-slate-500 italic" data-testid="aiworkspace-action-bar-empty">
                Loading action catalogue…
            </div>
        );
    }
    return (
        <div className="space-y-4" data-testid="aiworkspace-action-bar">
            {FAMILY_ORDER.map(family => {
                const familyActions = actions.filter(a => a.family === family);
                if (familyActions.length === 0) return null;
                return (
                    <div key={family} data-testid={`aiworkspace-family-${family}`}>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                            {FAMILY_LABELS[family]}
                        </h3>
                        <div className="flex flex-col gap-1.5">
                            {familyActions.map(action => {
                                const isActive = action.action_key === activeActionKey;
                                return (
                                    <Button
                                        key={action.action_key}
                                        variant={isActive ? "default" : "outline"}
                                        size="sm"
                                        className={`justify-start text-left h-auto py-2 ${
                                            isActive ? "bg-teal text-white hover:bg-teal/90" : ""
                                        }`}
                                        onClick={() => onSelectAction(action.action_key)}
                                        disabled={disabled}
                                        data-testid={`aiworkspace-action-${action.action_key}`}
                                    >
                                        <div className="flex flex-col items-start">
                                            <span className="font-medium text-sm">{action.ui_label}</span>
                                            <span className="text-xs opacity-70 text-left whitespace-normal mt-0.5">
                                                {action.ui_description}
                                            </span>
                                        </div>
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
